import React from 'react'
import { SubscriptionResponse } from '@/types/subscription'
import { CheckCircleIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline'
import { cn } from '@/utils/cn'

interface SubscriptionStatusBadgeProps {
    subscription: SubscriptionResponse | null
    className?: string
}

export const SubscriptionStatusBadge: React.FC<SubscriptionStatusBadgeProps> = ({
    subscription,
    className,
}) => {
    if (!subscription) {
        return (
            <div className={cn('inline-flex items-center gap-2', className)}>
                <div className="w-3 h-3 rounded-full bg-gray-300" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">No Active Subscription</span>
            </div>
        )
    }

    const tierLabels: Record<string, string> = {
        free: 'Free',
        pro: 'Pro Member',
        enterprise: 'Enterprise',
        business: 'Business',
    }

    const statusConfig = {
        active: {
            color: 'bg-green-100 dark:bg-green-900/30',
            textColor: 'text-green-700 dark:text-green-300',
            dotColor: 'bg-green-500',
            icon: CheckCircleIcon,
        },
        inactive: {
            color: 'bg-gray-100 dark:bg-gray-800',
            textColor: 'text-gray-700 dark:text-gray-300',
            dotColor: 'bg-gray-500',
            icon: ClockIcon,
        },
        cancelled: {
            color: 'bg-red-100 dark:bg-red-900/30',
            textColor: 'text-red-700 dark:text-red-300',
            dotColor: 'bg-red-500',
            icon: ExclamationTriangleIcon,
        },
        expired: {
            color: 'bg-yellow-100 dark:bg-yellow-900/30',
            textColor: 'text-yellow-700 dark:text-yellow-300',
            dotColor: 'bg-yellow-500',
            icon: ExclamationTriangleIcon,
        },
        pending: {
            color: 'bg-blue-100 dark:bg-blue-900/30',
            textColor: 'text-blue-700 dark:text-blue-300',
            dotColor: 'bg-blue-500',
            icon: ClockIcon,
        },
    }

    const config = statusConfig[subscription.status as keyof typeof statusConfig] || statusConfig.inactive
    const Icon = config.icon

    return (
        <div className={cn(
            'inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-medium',
            config.color,
            className
        )}>
            <div className="flex items-center gap-2">
                <Icon className={cn('w-4 h-4', config.textColor)} />
                <span className={cn('text-sm', config.textColor)}>
                    {tierLabels[subscription.tier] || subscription.tier} · {subscription.status}
                </span>
            </div>
        </div>
    )
}